import copy from '../copy/en/form-fallback.json' with { type: 'json' };

/** Discover only the server's public destination; never forward answers or private links. */
export function initializeFormFallback(canNavigate: () => boolean = () => true): () => void {
  const query = new URLSearchParams(window.location.search);
  if (!['/', '/forms/agreement', '/forms/agreement/'].includes(window.location.pathname)
    || ['token', 'signingToken', 'bundle', 'lc', 'revision'].some(key => query.has(key))) return () => {};
  const invitations = query.getAll('invitation');
  if (invitations.length > 1 || invitations.some(value => !/^[A-Za-z0-9_-]{24}$/.test(value))) return () => {};
  const mount = document.querySelector<HTMLElement>('#formFallback') ?? document.querySelector<HTMLElement>('.footer-inner');
  if (!mount) return () => {};
  let disposed = false;
  const controller = new AbortController();
  const footer = document.createElement('div'); footer.className = 'form-fallback';
  const params = new URLSearchParams();
  if (invitations[0]) params.set('invitation', invitations[0]);
  const suffix = params.size ? `?${params}` : '';
  const prompt = document.createElement('span'); prompt.className = 'form-fallback__prompt'; prompt.textContent = copy.prompt;
  const link = document.createElement('a'); link.className = 'form-fallback__link'; link.textContent = copy.link;
  link.href = `/form-fallback${suffix}`; link.referrerPolicy = 'no-referrer'; link.target = '_self';
  const notice = document.createElement('p'); notice.id = 'formFallbackNotice'; notice.className = 'form-fallback__notice';
  notice.textContent = query.getAll('preview').length === 1 && query.get('preview') === '1' ? copy.previewNotice : copy.notice;
  link.setAttribute('aria-describedby', notice.id);
  const error = document.createElement('p'); error.className = 'form-fallback__notice'; error.setAttribute('role', 'status');
  let pending = false;
  link.addEventListener('click', event => {
    if (!canNavigate()) { event.preventDefault(); return; }
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (pending || disposed) return;
    pending = true; error.textContent = ''; link.setAttribute('aria-disabled', 'true');
    void fetch(`/api/form-fallback${suffix}`, { credentials: 'omit', signal: controller.signal }).then(async response => {
      if (!response.ok) throw new Error('unavailable');
      const data: unknown = await response.json();
      if (disposed || !canNavigate()) return;
      if (!data || typeof data !== 'object' || !('url' in data) || typeof data.url !== 'string') throw new Error('unavailable');
      const url = new URL(data.url);
      if (url.protocol !== 'https:' || url.username || url.password
        || !['form.jotform.com', 'www.jotform.com', 'submit.jotform.com'].includes(url.hostname)) throw new Error('unavailable');
      if (query.get('embed') === '1' && window.parent !== window) {
        window.parent.postMessage({ type: 'ohf:signer:fallback' }, window.location.origin);
      } else window.location.assign(url.toString());
    }).catch(() => { if (!disposed) error.textContent = copy.unavailable; })
      .finally(() => { pending = false; link.removeAttribute('aria-disabled'); });
  });
  footer.append(prompt, document.createTextNode(' '), link, notice, error); mount.append(footer);
  return () => { disposed = true; controller.abort(); footer.remove(); };
}
